import { useState } from "react";
import { toast } from "sonner";
import { postEnquiry, postPartnerEnquiry } from "../lib/api";
import { STUDY_LEVELS } from "../data/static";
import Icon from "./Icon";

const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 transition-colors duration-200 focus:border-navy focus:outline-none";

const COUNTRIES = ["USA", "UK", "Canada", "Australia", "New Zealand", "Germany", "Singapore", "Malaysia", "Mauritius", "Not sure yet"];
const ORG_TYPES = ["University", "College", "Education Provider", "Recruitment Partner", "Institutional Representative", "Other"];

export function Field({ label, required, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label} {required && <span className="text-dot-red">*</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-dot-red" role="alert">{error}</span>}
    </label>
  );
}

function useForm(initial, requiredFields, submitFn, successTestId) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    requiredFields.forEach((k) => {
      if (!form[k].trim()) errs[k] = "This field is required";
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email address";
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please check the highlighted fields and try again.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitFn(form);
      setDone(true);
      toast.success(res.message || "Your enquiry has been submitted successfully.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return { form, errors, submitting, done, set, submit };
}

function SuccessPanel({ testid }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center" data-testid={testid}>
      <span className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-dot-green/10">
        <Icon name="CheckCircle2" className="h-7 w-7 text-dot-green" />
      </span>
      <h3 className="font-display text-2xl font-semibold text-navy">Thank you.</h3>
      <p className="mt-2 max-w-sm text-sm text-slate-600">Your enquiry has been submitted successfully. Our team will be in touch shortly.</p>
    </div>
  );
}

export function ContactForm() {
  const { form, errors, submitting, done, set, submit } = useForm(
    { fullName: "", email: "", phone: "", preferredCountry: "", studyLevel: "", course: "", message: "" },
    ["fullName", "email"],
    postEnquiry
  );

  if (done) return <SuccessPanel testid="contact-form-success" />;

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate data-testid="contact-form">
      <Field label="Full Name" required error={errors.fullName}>
        <input data-testid="contact-fullname-input" className={inputCls} value={form.fullName} onChange={set("fullName")} placeholder="Your full name" />
      </Field>
      <Field label="Email" required error={errors.email}>
        <input data-testid="contact-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
      </Field>
      <Field label="Phone">
        <input data-testid="contact-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+00 000 000 000" />
      </Field>
      <Field label="Preferred Country">
        <select data-testid="contact-country-select" className={inputCls} value={form.preferredCountry} onChange={set("preferredCountry")}>
          <option value="">Select country</option>
          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Study Level">
        <select data-testid="contact-level-select" className={inputCls} value={form.studyLevel} onChange={set("studyLevel")}>
          <option value="">Select level</option>
          {STUDY_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </Field>
      <Field label="Course / Field">
        <input data-testid="contact-course-input" className={inputCls} value={form.course} onChange={set("course")} placeholder="e.g. Business, Engineering" />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Message">
          <textarea data-testid="contact-message-input" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your plans" />
        </Field>
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="sm:col-span-2 flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-navy-light disabled:opacity-60"
        data-testid="contact-submit-button"
      >
        {submitting ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Send" className="h-4 w-4" />}
        {submitting ? "Submitting..." : "Submit Enquiry"}
      </button>
    </form>
  );
}

export function PartnerForm() {
  const { form, errors, submitting, done, set, submit } = useForm(
    { organisationName: "", contactPerson: "", email: "", phone: "", country: "", organisationType: "", website: "", message: "" },
    ["organisationName", "contactPerson", "email"],
    postPartnerEnquiry
  );

  if (done) return <SuccessPanel testid="partner-form-success" />;

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate data-testid="partner-form">
      <Field label="Organisation Name" required error={errors.organisationName}>
        <input data-testid="partner-org-input" className={inputCls} value={form.organisationName} onChange={set("organisationName")} placeholder="Your institution or organisation" />
      </Field>
      <Field label="Contact Person" required error={errors.contactPerson}>
        <input data-testid="partner-contact-input" className={inputCls} value={form.contactPerson} onChange={set("contactPerson")} placeholder="Full name" />
      </Field>
      <Field label="Email" required error={errors.email}>
        <input data-testid="partner-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@institution.edu" />
      </Field>
      <Field label="Phone">
        <input data-testid="partner-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+00 000 000 000" />
      </Field>
      <Field label="Country">
        <input data-testid="partner-country-input" className={inputCls} value={form.country} onChange={set("country")} placeholder="Country" />
      </Field>
      <Field label="Organisation Type">
        <select data-testid="partner-type-select" className={inputCls} value={form.organisationType} onChange={set("organisationType")}>
          <option value="">Select type</option>
          {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Website">
          <input data-testid="partner-website-input" type="url" className={inputCls} value={form.website} onChange={set("website")} placeholder="https://" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Message">
          <textarea data-testid="partner-message-input" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your partnership interest" />
        </Field>
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="sm:col-span-2 flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-navy-light disabled:opacity-60"
        data-testid="partner-submit-button"
      >
        {submitting ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Send" className="h-4 w-4" />}
        {submitting ? "Submitting..." : "Submit Partnership Enquiry"}
      </button>
    </form>
  );
}

const PROGRAM_OPTIONS = ["Foundation", "Diploma", "English Prep", "Pre-Master's"];
const DESTINATION_OPTIONS = ["Singapore", "Australia", "UK", "Canada", "Other"];

function CheckPill({ label, checked, onToggle, testid }) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
        checked ? "border-navy bg-navy text-white" : "border-slate-200 bg-white text-slate-700 hover:border-navy/40"
      }`}
      data-testid={testid}
    >
      <input type="checkbox" className="sr-only" checked={checked} onChange={onToggle} aria-label={label} />
      <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${checked ? "border-white bg-white/20" : "border-slate-300"}`}>
        {checked && <Icon name="CheckCircle2" className="h-3.5 w-3.5 text-white" />}
      </span>
      {label}
    </label>
  );
}

export function PathwayEnquiryForm() {
  const EMPTY = {
    fullName: "", email: "", phone: "", nationality: "",
    highestQualification: "", intendedIntake: "",
    interestedPrograms: [], preferredDestinations: [],
  };
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k, v) => () =>
    setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    ["fullName", "phone", "nationality", "highestQualification", "intendedIntake"].forEach((k) => {
      if (!form[k].trim()) errs[k] = "This field is required";
    });
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email address";
    if (!form.interestedPrograms.length) errs.interestedPrograms = "Select at least one program";
    if (!form.preferredDestinations.length) errs.preferredDestinations = "Select at least one destination";
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please check the highlighted fields and try again.");
      return;
    }
    setSubmitting(true);
    try {
      const { postPathwayEnquiry } = await import("../lib/api");
      await postPathwayEnquiry(form);
      setDone(true);
      toast.success("Thank you. Our education advisor will contact you shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return <SuccessPanel testid="pathway-form-success" />;

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate data-testid="pathway-enquiry-form">
      <Field label="Full Name" required error={errors.fullName}>
        <input data-testid="pathway-fullname-input" className={inputCls} value={form.fullName} onChange={set("fullName")} placeholder="Enter your full name" />
      </Field>
      <Field label="Email Address" required error={errors.email}>
        <input data-testid="pathway-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="Enter your email address" />
      </Field>
      <Field label="Phone Number" required error={errors.phone}>
        <input data-testid="pathway-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Enter your phone number" />
      </Field>
      <Field label="Nationality" required error={errors.nationality}>
        <input data-testid="pathway-nationality-input" className={inputCls} value={form.nationality} onChange={set("nationality")} placeholder="e.g. Sri Lankan, Indian, Mauritian" />
      </Field>
      <Field label="Highest Qualification" required error={errors.highestQualification}>
        <input data-testid="pathway-qualification-input" className={inputCls} value={form.highestQualification} onChange={set("highestQualification")} placeholder="Enter your highest qualification" />
      </Field>
      <Field label="Intended Intake (Month/Year)" required error={errors.intendedIntake}>
        <input data-testid="pathway-intake-input" type="month" className={inputCls} value={form.intendedIntake} onChange={set("intendedIntake")} />
      </Field>
      <div className="sm:col-span-2">
        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          Interested Program <span className="text-dot-red">*</span>
        </span>
        <div className="flex flex-wrap gap-2.5">
          {PROGRAM_OPTIONS.map((p) => (
            <CheckPill key={p} label={p} checked={form.interestedPrograms.includes(p)} onToggle={toggle("interestedPrograms", p)} testid={`pathway-program-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
          ))}
        </div>
        {errors.interestedPrograms && <span className="mt-1 block text-xs text-dot-red" role="alert">{errors.interestedPrograms}</span>}
      </div>
      <div className="sm:col-span-2">
        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          Your Preferred Study Destination <span className="text-dot-red">*</span>
        </span>
        <div className="flex flex-wrap gap-2.5">
          {DESTINATION_OPTIONS.map((d) => (
            <CheckPill key={d} label={d} checked={form.preferredDestinations.includes(d)} onToggle={toggle("preferredDestinations", d)} testid={`pathway-destination-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
          ))}
        </div>
        {errors.preferredDestinations && <span className="mt-1 block text-xs text-dot-red" role="alert">{errors.preferredDestinations}</span>}
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="sm:col-span-2 flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-navy-light disabled:opacity-60"
        data-testid="pathway-submit-button"
      >
        {submitting ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Send" className="h-4 w-4" />}
        {submitting ? "Submitting..." : "Submit Now"}
      </button>
    </form>
  );
}
